import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled
} from '@mui/material';
import { ToggleButton as MuiToggleButton, ToggleButtonGroup } from '@mui/material';
import { MdCheckCircleOutline } from 'react-icons/md';
import ChatGptPlanIcon from 'assets/icons/ChatGptPlanIcon';

// Styled components for the UI
const PopularTag = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -12,
  right: 24,
  backgroundColor: '#7233EB',
  color: 'white',
  padding: '4px 12px',
  borderRadius: 16,
  fontSize: 12,
  fontWeight: 500
}));

const PlanPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'popular'
})<{ popular?: boolean }>(({ popular, theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  padding: 24,
  height: '100%',
  borderRadius: 16,
  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
  border: popular ? '1px solid #7233EB' : '1px solid #f0f0f0',
  transition: 'all 0.3s ease'
}));

const IconBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 48,
  width: 48,
  borderRadius: '50%',
  backgroundColor: '#f5f0ff',
  marginBottom: 16,
  marginLeft: 'auto',
  marginRight: 'auto'
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: 12,
  '&:last-child': {
    marginBottom: 0
  }
}));

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  backgroundColor: '#f5f0ff',
  padding: '4px',
  borderRadius: '24px'
}));

const StyledToggleButton = styled(MuiToggleButton)(({ theme }) => ({
  padding: '8px 24px',
  borderRadius: '24px !important',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '14px',
  color: '#757575',
  border: 'none',
  '&.Mui-selected': {
    backgroundColor: 'white',
    color: '#7233EB',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '&:hover': {
      backgroundColor: 'white'
    }
  },
  '&:hover': {
    backgroundColor: 'transparent',
    color: '#7233EB'
  }
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: 16,
  backgroundColor: 'white',
  borderTopLeftRadius: 8,
  borderTopRightRadius: 8,
  borderBottom: 0,
  borderLeft: '1px solid rgba(224, 224, 224, 1)',
  borderRight: '1px solid rgba(224, 224, 224, 1)',
  borderTop: '1px solid rgba(224, 224, 224, 1)'
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderBottomLeftRadius: 8,
  borderBottomRightRadius: 8,
  marginBottom: 32,
  '& .MuiTableCell-root': {
    padding: '12px 16px'
  }
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: 24,
  height: '100%',
  borderRadius: 16,
  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
  border: '1px solid #f0f0f0'
}));

const Plans = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  // Toggle Button Component
  const ToggleButtonComponent = ({
    value,
    onChange,
    options
  }: {
    value: string;
    onChange: (value: string) => void;
    options: { value: string; label: string }[];
  }) => {
    const handleChange = (event: React.MouseEvent<HTMLElement>, newValue: string) => {
      if (newValue !== null) {
        onChange(newValue);
      }
    };

    return (
      <StyledToggleButtonGroup value={value} exclusive onChange={handleChange} aria-label='billing cycle'>
        {options.map((option) => (
          <StyledToggleButton key={option.value} value={option.value}>
            {option.label}
          </StyledToggleButton>
        ))}
      </StyledToggleButtonGroup>
    );
  };

  // Pricing Plan Component
  const PricingPlan = ({
    title,
    icon,
    description,
    price,
    popular = false,
    features,
    ctaText,
    isEnterprise = false
  }: {
    title: string;
    icon: React.ReactNode;
    description: string;
    price: number | string;
    popular?: boolean;
    features: string[];
    ctaText: string;
    isEnterprise?: boolean;
  }) => {
    return (
      <PlanPaper popular={popular} elevation={0}>
        {popular && <PopularTag>Most Popular</PopularTag>}

        <IconBox>{icon}</IconBox>

        <Typography variant='h5' fontWeight='bold' textAlign='center' gutterBottom>
          {title}
        </Typography>

        <Typography variant='body2' color='text.secondary' textAlign='center' mb={2}>
          {description}
        </Typography>

        <Box textAlign='center' mb={3}>
          <Typography variant='h4' component='span' fontWeight='bold'>
            {typeof price === 'number' ? `$${price}` : price}
          </Typography>
          {typeof price === 'number' && (
            <Typography variant='body2' component='span' color='text.secondary'>
              /mo
            </Typography>
          )}
        </Box>

        <Box mb={3}>
          {features.map((feature, index) => (
            <FeatureItem key={index}>
              <MdCheckCircleOutline />
              <Typography variant='body2' color='text.secondary'>
                {feature}
              </Typography>
            </FeatureItem>
          ))}
        </Box>
        <Box mt='auto'>
          <a href='mailto:vishal@kickcall.ai' style={{ textDecoration: 'none' }}>
            <Button
              fullWidth
              variant={popular ? 'contained' : 'outlined'}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                py: 1,
                backgroundColor: popular ? '#7233EB' : 'transparent',
                borderColor: popular ? '#7233EB' : 'rgba(0, 0, 0, 0.23)',
                color: popular ? 'white' : 'text.primary',
                '&:hover': {
                  backgroundColor: popular ? '#6029c5' : 'rgba(0, 0, 0, 0.04)',
                  borderColor: popular ? '#6029c5' : 'rgba(0, 0, 0, 0.23)'
                }
              }}
            >
              {ctaText}
            </Button>
          </a>
        </Box>
      </PlanPaper>
    );
  };

  // Feature Comparison Table Component
  const FeatureComparisonTable = ({ billing }: { billing: 'monthly' | 'yearly' }) => {
    const sections = [
      {
        id: 'core',
        title: 'Core Capabilities & Cost Structure',
        icon: (
          <Typography fontSize='small' color='#7233EB'>
            $
          </Typography>
        ),
        features: [
          {
            name: 'Minutes Included',
            growth: '4000',
            agency: '8000',
            enterprise: 'Custom'
          },
          {
            name: 'Extra Minute',
            growth: '$0.10',
            agency: '$0.10',
            enterprise: 'Custom'
          },
          {
            name: 'Characterized Voice Engine API',
            growth: true,
            agency: true,
            enterprise: true
          },
          {
            name: 'New Phone Number',
            growth: '$1/ea',
            agency: '$1/ea',
            enterprise: 'Included'
          },
          {
            name: 'Unlimited Agents',
            growth: true,
            agency: true,
            enterprise: true
          },
          {
            name: 'Multi-Language',
            growth: true,
            agency: true,
            enterprise: true
          },
          {
            name: 'Scripting',
            growth: true,
            agency: true,
            enterprise: true
          },
          {
            name: 'In-App Task Monitoring',
            growth: true,
            agency: true,
            enterprise: true
          }
        ]
      },
      {
        id: 'actions',
        title: 'Actions',
        icon: (
          <Typography fontSize='small' color='#7233EB'>
            ⚡
          </Typography>
        ),
        features: [
          {
            name: 'Send SMS',
            growth: true,
            agency: true,
            enterprise: true
          },
          {
            name: 'Real-Time Booking',
            growth: true,
            agency: true,
            enterprise: true
          },
          {
            name: 'Call Transfer',
            growth: true,
            agency: true,
            enterprise: true
          },
          {
            name: 'Information Extractor',
            growth: true,
            agency: true,
            enterprise: true
          },
          {
            name: 'Custom Actions',
            growth: true,
            agency: true,
            enterprise: true
          }
        ]
      },
      {
        id: 'whitelabel',
        title: 'White Label',
        icon: (
          <Typography fontSize='small' color='#7233EB'>
            🏷️
          </Typography>
        ),
        features: [
          {
            name: 'White Label Platform',
            growth: true,
            agency: true,
            enterprise: true
          },
          {
            name: 'Subaccounts',
            growth: '25',
            agency: '500+',
            enterprise: 'Custom'
          }
        ]
      },
      {
        id: 'integrations',
        title: 'Integrations',
        icon: (
          <Typography fontSize='small' color='#7233EB'>
            🔌
          </Typography>
        ),
        features: [
          {
            name: 'Rest API',
            growth: 'Coming Soon',
            agency: 'Coming Soon',
            enterprise: 'Coming Soon'
          },
          {
            name: 'All Existing Integrations',
            growth: true,
            agency: true,
            enterprise: true
          },
          {
            name: 'Custom Integrations',
            growth: false,
            agency: true,
            enterprise: true
          },
          {
            name: '3rd Party Integrations',
            growth: false,
            agency: false,
            enterprise: true
          }
        ]
      },
      {
        id: 'support',
        title: 'Support',
        icon: (
          <Typography fontSize='small' color='#7233EB'>
            🛟
          </Typography>
        ),
        features: [
          {
            name: 'Dedicated Support & Slack Channel',
            growth: '$1000/mo',
            agency: '$1000/mo',
            enterprise: true
          },
          {
            name: 'Early Access to New Features',
            growth: true,
            agency: true,
            enterprise: true
          },
          {
            name: 'Solution Architect',
            growth: false,
            agency: false,
            enterprise: true
          }
        ]
      },
      {
        id: 'compliance',
        title: 'Compliance',
        icon: (
          <Typography fontSize='small' color='#7233EB'>
            🔒
          </Typography>
        ),
        features: [
          {
            name: 'Guaranteed Uptime (SLA)',
            growth: false,
            agency: false,
            enterprise: true
          },
          {
            name: 'HIPAA',
            growth: false,
            agency: false,
            enterprise: true
          },
          {
            name: 'SOC2 Security',
            growth: true,
            agency: true,
            enterprise: true
          },
          {
            name: 'GDPR',
            growth: true,
            agency: true,
            enterprise: true
          }
        ]
      }
    ];

    const renderValue = (value: string | boolean | number) => {
      if (typeof value === 'boolean') {
        return value ? <MdCheckCircleOutline fontSize='small' /> : null;
      }
      return <Typography variant='body2'>{value}</Typography>;
    };

    return (
      <Box mb={12}>
        <Typography variant='h5' fontWeight='bold' textAlign='left' mb={1}>
          Feature Comparison
        </Typography>
        <Typography variant='body2' color='text.secondary' textAlign='left' mb={4}>
          Detailed breakdown of what's included in each plan
        </Typography>

        {sections.map((section) => (
          <Box key={section.id} mb={4}>
            <SectionHeader>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 24,
                  width: 24,
                  borderRadius: '50%',
                  backgroundColor: '#f5f0ff'
                }}
              >
                {section.icon}
              </Box>
              <Typography fontWeight={600} color='text.primary'>
                {section.title}
              </Typography>
            </SectionHeader>

            <StyledTableContainer sx={{ borderTop: 0 }}>
              <Table>
                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell>Feature</TableCell>
                    <TableCell align='center'>Growth</TableCell>
                    <TableCell align='center'>Agency</TableCell>
                    <TableCell align='center'>Enterprise</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {section.features.map((feature, index) => (
                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                      <TableCell component='th' scope='row'>
                        <Typography variant='body2'>{feature.name}</Typography>
                      </TableCell>
                      <TableCell align='center'>{renderValue(feature.growth)}</TableCell>
                      <TableCell align='center'>{renderValue(feature.agency)}</TableCell>
                      <TableCell align='center'>{renderValue(feature.enterprise)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </Box>
        ))}
      </Box>
    );
  };

  // Logo Component
  const Logo = ({ name }: { name: string }) => {
    const renderLogo = () => {
      switch (name) {
        case 'chatgpt':
          return <ChatGptPlanIcon />;
        case 'elevenLabs':
          return (
            <Box textAlign='center'>
              <Typography variant='body2' fontWeight='bold' color='#5e1afc' lineHeight={1} marginBottom={1}>
                XI
              </Typography>
              <Typography
                variant='caption'
                lineHeight={1}
                sx={{ mt: -0.5, display: 'block', fontSize: '0.65rem', fontWeight: 900 }}
              >
                ELEVEN LABS
              </Typography>
            </Box>
          );
        case 'deepGram':
          return (
            <Box display='flex' alignItems='center'>
              <Typography variant='caption' fontWeight='bold' sx={{ color: '#1DBAA0', fontSize: '0.75rem' }}>
                DEEP
              </Typography>
              <Typography variant='caption' fontWeight='bold' sx={{ color: '#10826F', fontSize: '0.75rem' }}>
                GRAM
              </Typography>
            </Box>
          );
        case 'twilio':
          return (
            <Typography variant='body2' fontWeight='bold' sx={{ color: '#F22F46', fontSize: '0.875rem' }}>
              twilio
            </Typography>
          );
        default:
          return (
            <Typography variant='body2' fontWeight='bold' sx={{ color: '#F22F46', fontSize: '0.875rem' }}>
              {name.toUpperCase()}
            </Typography>
          );
      }
    };

    return (
      <Box
        sx={{
          backgroundColor: 'white',
          p: 1.5,
          borderRadius: 2,
          boxShadow: 1,
          border: '1px solid',
          borderColor: 'divider',
          transition: 'all 0.2s ease',
          width: 80,
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': {
            boxShadow: 3
          }
        }}
      >
        {renderLogo()}
      </Box>
    );
  };

  // Value Prop Card Component
  const ValuePropCard = ({
    icon,
    title,
    description
  }: {
    icon: React.ReactNode;
    title: string;
    description: string;
  }) => {
    return (
      <StyledPaper elevation={0}>
        <Box mb={2}>{icon}</Box>
        <Typography variant='h6' fontWeight='bold' mb={1}>
          {title}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {description}
        </Typography>
      </StyledPaper>
    );
  };

  // Partner Section Component
  const PartnerSection = () => {
    return (
      <Box py={8} sx={{ backgroundColor: '#f9f5ff', borderRadius: 6, mx: -2, px: 2 }}>
        <Box maxWidth='md' mx='auto' mb={5} textAlign='center'>
          <Box
            height={40}
            width={40}
            sx={{ backgroundColor: '#f5f0ff', borderRadius: '50%' }}
            display='flex'
            alignItems='center'
            justifyContent='center'
            mx='auto'
            mb={2}
          >
            <Box height={20} width={20} sx={{ backgroundColor: '#7233EB', borderRadius: '50%' }} />
          </Box>

          <Typography variant='h4' fontWeight='bold' mb={2}>
            Your Business Partner, Not Just a Provider
          </Typography>

          <Typography variant='body1' color='text.secondary'>
            We exclusively focus on business-to-business relationships, understanding your unique needs and growth
            goals. Our partnership approach means we're invested in your success.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <ValuePropCard
              icon={<Box fontSize={32}>🚀</Box>}
              title='Growth-Focused Partnership'
              description="We're invested in your success. Our team works alongside yours to ensure optimal results and rapid growth."
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <ValuePropCard
              icon={<Box fontSize={32}>🛡️</Box>}
              title='Premium Support'
              description='Get dedicated support specialists who know your business and can help resolve issues instantly.'
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <ValuePropCard
              icon={<Box fontSize={32}>📱</Box>}
              title='Business Expertise'
              description='Benefit from our deep industry knowledge and optimizing your operations with our expert guidance.'
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <ValuePropCard
              icon={<Box fontSize={32}>💙</Box>}
              title='Long-term Relationship'
              description='We build lasting partnerships, not just transactions. Your success is our commitment.'
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <ValuePropCard
              icon={<Box fontSize={32}>⏱️</Box>}
              title='Rapid Response'
              description='Your technical business requests are handled with rapid response times and exact issue resolution.'
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', width: '100%', bgcolor: '#fbfaff' }}>
      <Container maxWidth='lg' sx={{ pt: 8, pb: 4 }}>
        <Box textAlign='center'>
          <Typography variant='h3' fontWeight='bold' color='#7233EB' gutterBottom>
            Choose Your Perfect Plan
          </Typography>
          <Typography variant='body1' color='text.secondary' mb={3}>
            Scalable solutions for businesses of all sizes - find the right plan for your needs
          </Typography>

          {/* Billing toggle */}
          <Box display='flex' justifyContent='center' mb={5}>
            <ToggleButtonComponent
              value={billingCycle}
              onChange={(value) => setBillingCycle(value as 'monthly' | 'yearly')}
              options={[
                { value: 'monthly', label: 'Monthly' },
                { value: 'yearly', label: 'Yearly' }
              ]}
            />
          </Box>

          {/* Pricing cards */}
          <Grid container spacing={3} mb={10}>
            <Grid item xs={12} md={4}>
              <PricingPlan
                title='Growth'
                icon={<MdCheckCircleOutline />}
                description='For businesses scaling to unlock advanced features'
                price={billingCycle === 'monthly' ? 900 : 810}
                popular={false}
                features={[
                  '4,000 mins, then $0.10/min',
                  '50 Concurrent Calls',
                  'Subaccounts',
                  'White Label Platform',
                  'White Label Reports',
                  'Access to New Features'
                ]}
                ctaText='Contact Sales'
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <PricingPlan
                title='Agency'
                icon={<MdCheckCircleOutline />}
                description='For agencies and larger businesses with high volume'
                price={billingCycle === 'monthly' ? 1400 : 1260}
                popular={true}
                features={[
                  '8,000 mins, then $0.10/min',
                  '80 Concurrent Calls',
                  'Subaccounts',
                  'White Label Platform',
                  '90-Day Onboarding & Private Slack Channel',
                  'Access to New Features'
                ]}
                ctaText='Contact Us'
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <PricingPlan
                title='Enterprise'
                icon={<MdCheckCircleOutline />}
                description='For enterprise requiring custom solutions and SLAs'
                price={'Custom'}
                popular={false}
                features={[
                  'Custom mins and pricing',
                  'Custom Concurrent Calls',
                  'Subaccounts',
                  'White Label Platform',
                  'Dedicated Support',
                  'Guaranteed Uptime (SLA)'
                ]}
                ctaText='Contact Us'
                isEnterprise={true}
              />
            </Grid>
          </Grid>

          {/* Trusted by logos */}
          <Box mb={12}>
            <Typography variant='subtitle2' color='text.secondary' mb={3}>
              TRUSTED BY
            </Typography>
            <Box display='flex' justifyContent='center' alignItems='center' gap={2} flexWrap='wrap'>
              <Logo name='elevenLabs' />
              <Logo name='deepGram' />
              <Logo name='chatgpt' />
              <Logo name='twilio' />
            </Box>
          </Box>

          {/* Feature comparison */}
          <FeatureComparisonTable billing={billingCycle} />

          {/* Partner section */}
          <PartnerSection />
        </Box>
      </Container>
    </Box>
  );
};

export default Plans;
